import { useEffect, useLayoutEffect, useRef } from 'react'
import GeoJSON from 'geojson'
import mapboxgl from 'mapbox-gl'

import { stackIds } from '../../containers/MapStackPresenter'
import { useMapContext } from '../MapView/MapContext'
import { isMapControlClick } from '../MapView/mapControlClick'
import { NavaidFeatureInfoData, projectNavaidFeature } from './navaidFormatters'
import {
  addNavaidSourceAndLayers,
  NAVAID_LAYER_IDS,
  registerNavaidImages,
  removeNavaidLayersAndSource,
  setNavaidSelectionFilter,
  setNavaidSourceData
} from './navaidLayerSpecs'

interface MapNavaidProps {
  data: GeoJSON.FeatureCollection<GeoJSON.Point>
  selectedNavaidId?: string
  onImageLoadError?: () => void
  onNavaidClick?: (navaid: NavaidFeatureInfoData) => void
  onLabelClick?: (navaid: NavaidFeatureInfoData) => void
}

function MapNavaid(props: MapNavaidProps): null {
  const { data, selectedNavaidId, onImageLoadError, onNavaidClick, onLabelClick } = props
  const { map } = useMapContext()
  const selectedNavaidIdRef = useRef(selectedNavaidId)
  const navaidClickRef = useRef(onNavaidClick)
  const labelClickRef = useRef(onLabelClick)
  const dataRef = useRef(data)

  useLayoutEffect(() => {
    if (!map) return

    let mounted = true

    void registerNavaidImages(map)
      .then((): void => {
        if (!mounted) return
        addNavaidSourceAndLayers(map, dataRef.current, stackIds.navaids, selectedNavaidIdRef.current)
      })
      .catch((): void => {
        if (mounted) onImageLoadError?.()
      })

    return () => {
      mounted = false
      removeNavaidLayersAndSource(map)
    }
  }, [map, onImageLoadError])

  useLayoutEffect(() => {
    dataRef.current = data
    if (!map) return
    setNavaidSourceData(map, data)
  }, [map, data])

  useLayoutEffect(() => {
    if (!map) return
    selectedNavaidIdRef.current = selectedNavaidId
    setNavaidSelectionFilter(map, selectedNavaidId)
  }, [map, selectedNavaidId])

  useEffect(() => {
    navaidClickRef.current = onNavaidClick
  }, [onNavaidClick])

  useEffect(() => {
    labelClickRef.current = onLabelClick
  }, [onLabelClick])

  useEffect(() => {
    if (!map) return

    const handleNavaidClick = (event: mapboxgl.MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      const feature = event.features?.[0]
      if (!feature) return

      const navaid = projectNavaidFeature(feature)
      if (!navaid) return

      navaidClickRef.current?.(navaid)
    }

    const handleLabelClick = (event: mapboxgl.MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      const feature = event.features?.[0]
      if (!feature) return

      const navaid = projectNavaidFeature(feature)
      if (!navaid) return

      labelClickRef.current?.(navaid)
    }

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', NAVAID_LAYER_IDS.icons, handleNavaidClick)
    map.on('click', NAVAID_LAYER_IDS.selectedIcons, handleNavaidClick)
    map.on('click', NAVAID_LAYER_IDS.labels, handleLabelClick)
    map.on(
      'mouseenter',
      [NAVAID_LAYER_IDS.icons, NAVAID_LAYER_IDS.selectedIcons, NAVAID_LAYER_IDS.labels],
      handleMouseEnter
    )
    map.on(
      'mouseleave',
      [NAVAID_LAYER_IDS.icons, NAVAID_LAYER_IDS.selectedIcons, NAVAID_LAYER_IDS.labels],
      handleMouseLeave
    )

    return () => {
      map.off('click', NAVAID_LAYER_IDS.icons, handleNavaidClick)
      map.off('click', NAVAID_LAYER_IDS.selectedIcons, handleNavaidClick)
      map.off('click', NAVAID_LAYER_IDS.labels, handleLabelClick)
      map.off(
        'mouseenter',
        [NAVAID_LAYER_IDS.icons, NAVAID_LAYER_IDS.selectedIcons, NAVAID_LAYER_IDS.labels],
        handleMouseEnter
      )
      map.off(
        'mouseleave',
        [NAVAID_LAYER_IDS.icons, NAVAID_LAYER_IDS.selectedIcons, NAVAID_LAYER_IDS.labels],
        handleMouseLeave
      )
    }
  }, [map])

  return null
}

export { MapNavaid }
